import { useQuery } from '@tanstack/react-query';
import { AlertCircle, History, Sparkles } from 'lucide-react';
import { activityApi, aiLogsApi, type AiLogRow } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export function ActivityView() {
  return (
    <Tabs defaultValue="activity" className="space-y-3">
      <TabsList>
        <TabsTrigger value="activity">Activity</TabsTrigger>
        <TabsTrigger value="ai-logs">AI Logs</TabsTrigger>
      </TabsList>
      <TabsContent value="activity">
        <ActivityLogPanel />
      </TabsContent>
      <TabsContent value="ai-logs">
        <AiLogsPanel />
      </TabsContent>
    </Tabs>
  );
}

function ActivityLogPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['activity'],
    queryFn: () => activityApi.list({ limit: 100 }),
    refetchInterval: 30_000,
  });

  if (isLoading) return <Card className="h-[400px] animate-pulse" />;
  if (error) return <ErrorCard title="Couldn't load activity" error={error} />;

  const items = data?.items ?? [];

  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border">
        {items.length === 0 && (
          <li className="px-4 py-12 text-center text-sm text-muted-foreground">
            <div className="flex flex-col items-center gap-2">
              <History className="h-6 w-6 text-muted-foreground/50" aria-hidden />
              <span>No activity logged yet.</span>
            </div>
          </li>
        )}
        {items.map((row) => (
          <li key={row.id} className="flex items-start gap-3 px-4 py-3">
            <div className="mt-0.5">
              <Badge variant="muted">{row.entityType}</Badge>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm">
                <span className="font-medium">{row.actorName}</span>{' '}
                <span className="text-muted-foreground">{formatLabel(row.action)}</span>{' '}
                {row.entityLabel && <span className="font-medium">{row.entityLabel}</span>}
              </div>
              {row.notes && (
                <div className="mt-0.5 text-[12px] text-muted-foreground">{row.notes}</div>
              )}
            </div>
            <time className="whitespace-nowrap text-[11px] text-muted-foreground">
              {new Date(row.createdAt).toLocaleString()}
            </time>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function AiLogsPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['ai-logs'],
    queryFn: () => aiLogsApi.list(100),
    refetchInterval: 30_000,
  });

  if (isLoading) return <Card className="h-[400px] animate-pulse" />;
  if (error) return <ErrorCard title="Couldn't load AI logs" error={error} />;

  const items = data?.items ?? [];

  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border">
        {items.length === 0 && (
          <li className="px-4 py-12 text-center text-sm text-muted-foreground">
            <div className="flex flex-col items-center gap-2">
              <Sparkles className="h-6 w-6 text-muted-foreground/50" aria-hidden />
              <span>No AI logs yet.</span>
            </div>
          </li>
        )}
        {items.map((row) => (
          <AiLogListItem key={row.id} row={row} />
        ))}
      </ul>
    </Card>
  );
}

function AiLogListItem({ row }: { row: AiLogRow }) {
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="mt-0.5">
        <Badge variant={aiStatusVariant(row.status)}>{formatLabel(row.status)}</Badge>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm">
          <span className="font-medium">{formatLabel(row.action_type)}</span>
          {row.task_id && <span className="text-muted-foreground"> on {row.task_id}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
          {row.model && <span>{row.model}</span>}
          {row.tokens_input !== null && <span>{row.tokens_input} input tokens</span>}
          {row.tokens_output !== null && <span>{row.tokens_output} output tokens</span>}
          {row.cost_cents !== null && <span>{row.cost_cents} cents</span>}
        </div>
        {row.error_message && (
          <div className="mt-1 text-[12px] text-destructive">{row.error_message}</div>
        )}
      </div>
      <time className="whitespace-nowrap text-[11px] text-muted-foreground">
        {new Date(row.updated_at).toLocaleString()}
      </time>
    </li>
  );
}

function ErrorCard({ title, error }: { title: string; error: unknown }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" aria-hidden />
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </Card>
  );
}

function formatLabel(value: string): string {
  return value.replaceAll('_', ' ');
}

function aiStatusVariant(
  status: AiLogRow['status'],
): 'success' | 'warning' | 'destructive' | 'muted' {
  if (status === 'success' || status === 'sent') return 'success';
  if (status === 'error') return 'destructive';
  if (status === 'discarded') return 'muted';
  return 'warning';
}
